import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient, useTheme } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  GraduationCap,
  Calendar,
  Sun,
  Moon,
  Settings,
  LogOut,
  CreditCard,
  GitCompare,
  ArrowLeft,
  Target,
  X,
  CheckCircle2,
  PlayCircle,
  Circle,
} from "lucide-react";
import { motion } from "framer-motion";

const ComparePage = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [colleges, setColleges] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  const fetchColleges = useCallback(async () => {
    try {
      const response = await apiClient.get("/colleges");
      setColleges(response.data);
    } catch (error) {
      console.error("Error fetching colleges:", error);
      toast.error("Failed to load colleges");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchColleges();
  }, [fetchColleges]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const toggleSelection = (collegeId) => {
    setSelectedIds((prev) => {
      if (prev.includes(collegeId)) {
        return prev.filter((id) => id !== collegeId);
      }
      if (prev.length >= 5) {
        toast.error("You can compare up to 5 schools at a time");
        return prev;
      }
      return [...prev, collegeId];
    });
    setCompareData(null);
  };

  const handleCompare = async () => {
    if (selectedIds.length < 2) {
      toast.error("Select at least 2 schools to compare");
      return;
    }

    setComparing(true);
    try {
      const response = await apiClient.post("/colleges/compare", {
        college_ids: selectedIds,
      });
      setCompareData(response.data);
    } catch (error) {
      console.error("Error comparing:", error);
      toast.error("Failed to compare schools");
    } finally {
      setComparing(false);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "reach":
        return "text-rose-600 dark:text-rose-400";
      case "target":
        return "text-amber-600 dark:text-amber-400";
      case "safety":
        return "text-emerald-600 dark:text-emerald-400";
      default:
        return "text-muted-foreground";
    }
  };

  const getCategoryBadge = (category) => {
    const config = {
      reach: { color: "bg-rose-500", label: "Reach" },
      target: { color: "bg-amber-500", label: "Target" },
      safety: { color: "bg-emerald-500", label: "Safety" },
    };
    const cat = config[category] || config.target;
    return (
      <span className="inline-flex items-center gap-1.5 text-xs">
        <span className={`w-2 h-2 rounded-full ${cat.color}`} />
        {cat.label}
      </span>
    );
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case "submitted":
        return (
          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-4 h-4" />
            Submitted
          </span>
        );
      case "in_progress":
        return (
          <span className="flex items-center gap-1 text-violet-600 dark:text-violet-400">
            <PlayCircle className="w-4 h-4" />
            In Progress
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Circle className="w-4 h-4" />
            Not Started
          </span>
        );
    }
  };

  const formatValue = (key, value) => {
    if (!value) return <span className="text-muted-foreground">—</span>;
    
    if (key === "category") return getCategoryBadge(value);
    if (key === "status") return getStatusDisplay(value);
    
    return value;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-8 h-8 text-primary" />
                <span className="font-bold text-xl font-outfit hidden sm:block">Admit List</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-btn"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/subscription")}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-outfit mb-2">
            Compare Schools
          </h1>
          <p className="text-muted-foreground">
            Select 2-5 schools to compare side by side.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* School Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="font-outfit flex items-center justify-between">
                  <span>Select Schools</span>
                  <span className="text-sm font-normal text-muted-foreground">
                    {selectedIds.length}/5 selected
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                {colleges.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No schools added yet</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => navigate("/dashboard")}
                    >
                      Add Schools
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {colleges.map((college) => (
                      <div
                        key={college.college_id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedIds.includes(college.college_id)
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-secondary/50"
                        }`}
                        onClick={() => toggleSelection(college.college_id)}
                        data-testid={`select-college-${college.college_id}`}
                      >
                        <Checkbox
                          checked={selectedIds.includes(college.college_id)}
                          onCheckedChange={() => toggleSelection(college.college_id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{college.name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {getCategoryBadge(college.category)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compare Button */}
            <div className="mt-4">
              <Button
                className="w-full rounded-full"
                onClick={handleCompare}
                disabled={selectedIds.length < 2 || comparing}
                data-testid="compare-btn"
              >
                {comparing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Comparing...
                  </>
                ) : (
                  <>
                    <GitCompare className="w-4 h-4 mr-2" />
                    Compare {selectedIds.length} Schools
                  </>
                )}
              </Button>
              {selectedIds.length > 0 && (
                <Button
                  variant="ghost"
                  className="w-full mt-2"
                  onClick={() => {
                    setSelectedIds([]);
                    setCompareData(null);
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Selection
                </Button>
              )}
            </div>
          </motion.div>

          {/* Comparison Table */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            {compareData ? (
              <Card>
                <CardHeader>
                  <CardTitle className="font-outfit">Comparison Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr>
                          <th className="text-left p-3 border-b border-border font-medium text-muted-foreground">
                            Field
                          </th>
                          {compareData.colleges.map((college) => (
                            <th
                              key={college.college_id}
                              className="text-left p-3 border-b border-border font-semibold min-w-[150px]"
                            >
                              <div className="flex flex-col">
                                <span className="truncate max-w-[140px]">{college.name}</span>
                                <span className={`text-xs font-normal ${getCategoryColor(college.category)}`}>
                                  {college.category?.charAt(0).toUpperCase() + college.category?.slice(1)}
                                </span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {compareData.comparison_fields
                          .filter((field) => field.key !== "name")
                          .map((field, index) => (
                            <tr
                              key={field.key}
                              className={index % 2 === 0 ? "bg-secondary/30" : ""}
                            >
                              <td className="p-3 border-b border-border text-sm font-medium text-muted-foreground">
                                {field.label}
                              </td>
                              {compareData.colleges.map((college) => (
                                <td
                                  key={college.college_id}
                                  className="p-3 border-b border-border text-sm"
                                >
                                  {formatValue(field.key, college[field.key])}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full min-h-[400px] flex items-center justify-center">
                <CardContent className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                    <GitCompare className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">No Comparison Yet</h3>
                  <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                    Select at least 2 schools from the list and click "Compare" to see a side-by-side comparison.
                  </p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav p-2">
        <div className="flex items-center justify-around">
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/dashboard")}
            data-testid="mobile-nav-dashboard"
          >
            <GraduationCap className="w-5 h-5" />
            <span className="text-xs">Dashboard</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/calendar")}
            data-testid="mobile-nav-calendar"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs">Calendar</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2 text-primary"
            onClick={() => navigate("/compare")}
            data-testid="mobile-nav-compare"
          >
            <GitCompare className="w-5 h-5" />
            <span className="text-xs">Compare</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/settings")}
            data-testid="mobile-nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default ComparePage;
